import { FocusEvent, KeyboardEvent, RefObject, SyntheticEvent } from 'react';
import KEYS from '@helpers/keys';

interface Props {
  inputRef: RefObject<HTMLInputElement | null>;
  isActive: boolean;
  unit: string;
  onValueChange: (newValue: number) => void;
  incrementValue?: number;
  blurOnlyWhenActive?: boolean;
  stopPropagationWhenActive?: boolean;
}

const useQuantityKeyHandlers = ({
  inputRef,
  isActive,
  unit,
  onValueChange,
  incrementValue = 1,
  blurOnlyWhenActive = false,
  stopPropagationWhenActive = false,
}: Props) => {
  const inputValue = (e: SyntheticEvent<HTMLInputElement>) => {
    if (unit === 'kilogram') {
      const forceDot = e.currentTarget.value.replace(',', '.');
      return parseFloat(forceDot.replace(/[^0-9.]/g, ''));
    }
    return e.currentTarget.value.replace(/[^0-9]/g, '');
  };

  const setCaretPos = (e: KeyboardEvent<HTMLInputElement>) => {
    const lastDigitPosition = inputValue(e).toString().length;
    inputRef.current?.setSelectionRange(lastDigitPosition, lastDigitPosition);
  };

  const markQuantity = (e: FocusEvent<HTMLInputElement>) => {
    const lastDigitPosition = inputValue(e).toString().length;
    inputRef.current?.setSelectionRange(0, lastDigitPosition);
  };

  const onKeyUpHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYS.ENTER:
      case KEYS.ESCAPE:
        if (!blurOnlyWhenActive || isActive) {
          return inputRef.current?.blur();
        }
        break;
      case KEYS.UP:
        if (isActive) onValueChange(+inputValue(e) + incrementValue);
        break;
      case KEYS.DOWN:
        if (isActive) onValueChange(+inputValue(e) - incrementValue);
        break;
      default:
        setCaretPos(e);
    }
    return false;
  };

  const onKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (stopPropagationWhenActive && isActive) {
      e.stopPropagation();
    }
  };

  return { onKeyUpHandler, onKeyDownHandler, inputValue, setCaretPos, markQuantity };
};

export default useQuantityKeyHandlers;
